import React, { useEffect, useState } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Copy, Plus, Trash2, KeyRound, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function AdminTeacherCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.getTeacherCodes();
      setCodes(list);
    } catch (e) {
      toast.error(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const doc = await api.createTeacherCode(note);
      toast.success(`Code généré : ${doc.code}`);
      setNote('');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try {
      await api.deleteTeacherCode(code);
      toast.success('Code supprimé');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copié !');
  };

  return (
    <div data-testid="admin-teacher-codes-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-black mb-1">Codes enseignant</h1>
          <p className="text-sm text-muted-foreground">Générer et gérer les codes d'accès pour les enseignants.</p>
        </div>
      </div>

      <form onSubmit={handleCreate} className="mc-card p-5 mb-6" data-testid="admin-create-code-form">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--primary-soft))' }}>
            <KeyRound className="w-5 h-5 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <h2 className="font-bold text-lg">Générer un nouveau code</h2>
            <p className="text-xs text-muted-foreground">Usage unique, à transmettre à l'enseignant.</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Note (ex: Pour M. Dupont, auto-école X)"
            className="flex-1 min-w-[200px]"
            data-testid="admin-create-code-note"
          />
          <Button type="submit" disabled={creating} className="mc-btn-primary" data-testid="admin-create-code-btn">
            <Plus className="w-4 h-4 mr-2" /> Générer
          </Button>
        </div>
      </form>

      <div className="mc-card overflow-x-auto" data-testid="admin-codes-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Utilisé par</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Créé le</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Chargement...</TableCell></TableRow>
            )}
            {!loading && codes.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Aucun code généré.</TableCell></TableRow>
            )}
            {codes.map(c => (
              <TableRow key={c.code} data-testid={`admin-code-row-${c.code}`}>
                <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                <TableCell>
                  {c.used ? (
                    <span className="mc-tag mc-tag-gray flex items-center gap-1 w-fit">
                      <CheckCircle2 className="w-3 h-3" /> Utilisé
                    </span>
                  ) : (
                    <span className="mc-tag mc-tag-green w-fit">Disponible</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.used_by || '—'}</TableCell>
                <TableCell className="text-sm">{c.note || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(c.code)}
                      data-testid={`admin-code-copy-${c.code}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(c.code)}
                      className="text-[hsl(var(--primary))]"
                      data-testid={`admin-code-delete-${c.code}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
